import { memo, useCallback, useMemo } from 'react'
import InsertDriveFileOutlined from '@material-ui/icons/InsertDriveFileOutlined'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import { usePostAppointmentContext } from '../../context/PostAppointmentContext/PostAppointmentProvider'
import { useAppointmentNoteFormRxFieldsEnabled } from '../../hooks/useAppointmentNoteFormRxFieldsEnabled'
import { parseReferToSpecialist } from '../../utils/refer-to-specialist.utils'
import { formatMMDDYYYY, formatTime } from '../../utils/time.utils'
import useAppointmentNoteAddenda from '../../hooks/useAppointmentNoteAddenda'
import NoteEditHistory from './NoteEditHistory'
import NoteForPatient from './NoteForPatient'
import { options as recommendedProgramOptions } from './RecommendedProgram'
import './styles.scss'

/**
 * Component for reviewing and displaying a complete appointment note.
 *
 * An appointment note is a complete document that includes multiple fields:
 * - Tests ordered before next visit
 * - Allara Rx interest
 * - Specialist referrals
 * - Follow-up timeline
 * - Recommended programs
 * - Note for patient (a specific text field where doctors write a message for the patient)
 * - Attachments
 *
 * The "note for patient" field is just one part of this larger document - see NoteForPatient.tsx
 * for more details about that specific field.
 */
const Review: React.FC = () => {
  const showRxInterestFields = useAppointmentNoteFormRxFieldsEnabled()
  const {
    selectedNote,
    labsResponse,
    imagingResponse,
    referToSpecialist,
    rxInterest,
    rxInterestMedications,
    timeline,
    noFollowUpReason,
    noFollowUpOtherReason,
    recommendedProgram,
    selectedFiles,
    isLoading,
    confirmAndSend,
    editSavedMessage,
    selectedAppointment,
  } = usePostAppointmentContext()

  const previouslySignedNote = useMemo(
    () => (selectedNote?.status === 'signed' ? selectedNote : null),
    [selectedNote]
  )

  const testOrderedText = useMemo(() => {
    const noFollowupReason =
      previouslySignedNote?.noFollowupReason ?? noFollowUpReason

    if (noFollowUpReason) {
      return `No followup - ${noFollowupReason}${
        noFollowUpOtherReason ? `: ${noFollowUpOtherReason}` : ''
      }`
    }

    const moreLabsOrdered = previouslySignedNote?.labsOrdered ?? labsResponse
    const moreImagingOrdered =
      previouslySignedNote?.imagingOrdered ?? imagingResponse

    if (moreLabsOrdered === undefined && moreImagingOrdered === undefined) {
      return undefined
    } else if (moreLabsOrdered && moreImagingOrdered) {
      return 'Labs & Imaging'
    } else if (moreLabsOrdered) {
      return 'Labs'
    } else if (moreImagingOrdered) {
      return 'Imaging'
    } else {
      return undefined
    }
  }, [
    previouslySignedNote,
    labsResponse,
    imagingResponse,
    noFollowUpReason,
    noFollowUpOtherReason,
  ])

  const rxInterestText = useMemo(() => {
    const interest = previouslySignedNote?.rxInterest ?? rxInterest
    const medications =
      previouslySignedNote?.rxInterestMedications ?? rxInterestMedications ?? []

    if (!interest || medications.length === 0) {
      return undefined
    }

    return medications.join(', ')
  }, [rxInterest, rxInterestMedications, previouslySignedNote])

  const referToSpecialistText = useMemo(
    () =>
      parseReferToSpecialist(
        previouslySignedNote?.referToSpecialist || referToSpecialist
      ).join(', '),
    [referToSpecialist, previouslySignedNote]
  )

  const followupInText = useMemo(() => {
    if (previouslySignedNote?.followup) {
      return previouslySignedNote.followup
    } else if (timeline) {
      return timeline
    } else {
      return undefined
    }
  }, [timeline, previouslySignedNote])

  const recommendedProgramText = useMemo(() => {
    const program =
      previouslySignedNote?.recommendedProgram ?? recommendedProgram

    if (program) {
      return (
        recommendedProgramOptions.find((o) => o.value === program)?.label ??
        undefined
      )
    } else {
      return undefined
    }
  }, [recommendedProgram, previouslySignedNote])

  const selectedFilesRemapped = useMemo(
    () => selectedFiles.map((file) => [...Array.from(file)]).flat(),
    [selectedFiles]
  )

  const handleSign = useCallback(
    () => confirmAndSend(selectedNote?.id),
    [selectedNote]
  )

  const handleSaveDraft = useCallback(
    () => confirmAndSend(selectedNote?.id, true),
    [selectedNote]
  )

  const { addenda: noteAddenda, isLoading: addendaLoading } =
    useAppointmentNoteAddenda(previouslySignedNote?.id)

  const originalAuthor = useMemo(() => {
    const provider =
      previouslySignedNote?.appointment?.provider ??
      selectedAppointment?.provider
    const name = [provider?.firstName, provider?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim()

    if (!name) {
      return 'Unknown'
    }

    return provider?.credentials ? `${name} (${provider.credentials})` : name
  }, [previouslySignedNote, selectedAppointment])

  const originalSubmittedAt = useMemo(() => {
    if (!previouslySignedNote?.timestamp) {
      return ''
    }

    const submitted = new Date(previouslySignedNote.timestamp)
    return `${formatMMDDYYYY(submitted)} ${formatTime(submitted)}`
  }, [previouslySignedNote])

  return (
    <div className="review align-self-center d-flex align-items-center justify-content-center flex-column w-100 mt-3">
      <div className="container d-flex flex-column gap-3">
        {previouslySignedNote && editSavedMessage && (
          <p className="note-updated-banner mb-0">{editSavedMessage}</p>
        )}
        {testOrderedText && (
          <div>
            <span className="label">Tests before next visit: </span>
            <span>{testOrderedText}</span>
          </div>
        )}
        {showRxInterestFields && rxInterestText && (
          <div>
            <span className="label">Interested in Allara Rx: </span>
            <span>{rxInterestText}</span>
          </div>
        )}
        {referToSpecialistText && (
          <div>
            <span className="label">Referal to specialist: </span>
            <span>{referToSpecialistText}</span>
          </div>
        )}
        {followupInText && (
          <div>
            <span className="label">Follow up in: </span>
            <span>{followupInText}</span>
          </div>
        )}
        {recommendedProgramText && (
          <div>
            <span className="label">Recommended program: </span>
            <span>{recommendedProgramText}</span>
          </div>
        )}
        <NoteForPatient />
        {!!(
          previouslySignedNote?.attachments?.length ||
          selectedFilesRemapped.length
        ) && (
          <section className="note-attachments" aria-label="Attachments">
            <p className="attachment-heading mb-2">Attachments</p>
            <div className="attachment-list">
              {previouslySignedNote
                ? previouslySignedNote.attachments?.map((file) => (
                    <div key={file.id} className="attachment-row">
                      <InsertDriveFileOutlined
                        className="attachment-icon"
                        aria-hidden="true"
                      />
                      <div className="attachment-details">
                        <a
                          href={file.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="attachment-name"
                        >
                          {file.fileName}
                        </a>
                      </div>
                    </div>
                  ))
                : selectedFilesRemapped.map((file, index) => (
                    <div key={index} className="attachment-row">
                      <InsertDriveFileOutlined
                        className="attachment-icon"
                        aria-hidden="true"
                      />
                      <div className="attachment-details">
                        <span className="attachment-name">{file.name}</span>
                        <span className="attachment-status is-pending">
                          Pending upload
                        </span>
                      </div>
                    </div>
                  ))}
            </div>
          </section>
        )}
        {previouslySignedNote && (
          <NoteEditHistory
            addenda={noteAddenda}
            isLoading={addendaLoading}
            author={originalAuthor}
            submittedAt={originalSubmittedAt}
            originalAttachments={previouslySignedNote.originalAttachments}
          />
        )}
      </div>
      {!previouslySignedNote && (
        <div className="d-flex gap-3">
          <SecondaryButton onClick={handleSaveDraft} theme="dark">
            Save as Draft
          </SecondaryButton>
          <PrimaryButton
            onClick={handleSign}
            disabled={isLoading}
            isLoading={isLoading}
            theme="dark"
          >
            Confirm & Send
          </PrimaryButton>
        </div>
      )}
    </div>
  )
}

export default memo(Review)
